import { type DeclarationBlocked, type DeclarationRequest } from '@api/vendor-portal';
import QuantityInput from '@components/ui/QuantityInput';
import ReportedByField from './ReportedByField';
import useToaster from '@hooks/toaster/useToaster';
import {
    Button,
    FileButton,
    Group,
    Loader,
    Modal,
    Select,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import {
    DownloadSimpleIcon,
    PlusIcon,
    TrashIcon,
    UploadSimpleIcon,
} from '@phosphor-icons/react';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { neutralColors, semanticColors } from '@styles/design-system';
import { useQuery } from '@tanstack/react-query';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';

import styles from './DeclareBatchesModal.module.css';
import {
    blankGridRow,
    buildOrderOptions,
    fillDownBatchCode,
    buildCumulativeDraw,
    checkDeclarations,
    declarationKey,
    findConflicts,
    groupRowsIntoBatches,
    nextGridRow,
    defaultOrderChoiceForProduct,
    parseProductionCsv,
    productionTemplateCsv,
    productOptionsForOrder,
    requestKey,
    resolveWorkOrder,
    type BatchGridRow,
    type OrderLine,
} from './batchGridRows';
import DeclarationBlockedPanel from './DeclarationBlockedPanel';
import { type OnBehalfOf, portalApi, portalScope } from './portalContext';
import { formatCount } from '@utils/currency';

interface DeclareBatchesModalProps {
    opened: boolean;
    onClose: () => void;
    onDeclared: () => void;
    onBehalfOf?: OnBehalfOf | null;
    /**
     * Who at the site reported these numbers, and the setter for it.
     *
     * Passed only by the brand. Supplying the pair is what makes the name block appear and
     * what gates the submit — a manufacturer writing in its own portal is identified by its
     * login and never sees it.
     */
    reportedBy?: string;
    onReportedByChange?: (value: string) => void;
    /** Open work order lines, so a row can say which order it answers. */
    orderLines?: OrderLine[];
    /**
     * Offer only the SKUs on `orderLines`, rather than everything the site is commissioned for.
     *
     * Set when the grid was opened from ONE order, where anything else in the catalogue is a
     * mistake waiting to be made: the picker was showing seven products for an order asking for
     * three. Not derivable from `orderLines` alone - the page header passes those too, just all
     * of them - so the call site says which question it is asking.
     *
     * A site that made something off-order in the same shift still records it, from the page
     * header's own grid, which is one click away and unrestricted.
     */
    restrictProductsToOrder?: boolean;
    /**
     * Whether a run may be recorded against no order at all.
     *
     * OFF FOR THE MANUFACTURER, on purpose: a run answers an order, and over-production is a
     * new production order from the brand rather than a silent pile. The brand's on-behalf
     * grid passes true — the same escape-hatch principle as its exemption from the product
     * cap — and keeps the "To stock" choice, the CSV's Stock keyword and the split-to-stock
     * fix for over-entries.
     */
    allowStock?: boolean;
    onRecordInward?: (materialIds: string[]) => void;
    onReleaseQc?: (materialIds: string[]) => void;
}

/**
 * A shift's worth of runs, entered at once.
 *
 * THE EVERYDAY PATH IS STILL THE SINGLE FORM. This is for the end of a shift, when what exists
 * is a sheet of runs rather than one run being recorded as it finishes — so it is reached from
 * its own button and does not replace the form that handles the common case well.
 *
 * A ROW IS A BATCH AND A SKU, and rows sharing a code become one batch. That matches the sheet
 * the numbers arrive on, and it keeps the grid flat: the alternative, batches containing SKUs,
 * nests a list inside a list and gets unreadable at four runs.
 *
 * ONE CUMULATIVE DRAW, NOT ONE PER BATCH. Every preview is computed against current stock, so
 * batches checked independently each see the whole shelf and all of them look feasible right up
 * until the last one fails. Summing what they need and comparing once is the only way a
 * shortfall is visible while it can still be fixed.
 */
const DeclareBatchesModal: FC<DeclareBatchesModalProps> = ({
    opened,
    onClose,
    onDeclared,
    reportedBy,
    onReportedByChange,
    onBehalfOf,
    orderLines = [],
    restrictProductsToOrder = false,
    allowStock = false,
    onRecordInward,
    onReleaseQc,
}) => {
    const { showToastSuccess, showToastError } = useToaster();
    const api = useMemo(() => portalApi(onBehalfOf), [onBehalfOf]);
    const scope = portalScope(onBehalfOf);

    /**
     * What a fresh row is Against before anybody picks a product: the order this grid was
     * opened for, when it was opened for exactly one. "To stock" as the opening word on an
     * order's own grid read as the system suggesting the wrong thing.
     */
    const singleOrderChoice = useMemo(() => {
        const orderIds = new Set(
            orderLines.map((line) => line.productionOrderId).filter(Boolean),
        );
        if (orderIds.size === 1 && orderLines.every((line) => line.productionOrderId)) {
            return `po:${[...orderIds][0]}`;
        }
        if (orderIds.size === 0 && orderLines.length === 1) {
            return `wo:${orderLines[0].workOrderId}`;
        }
        return '';
    }, [orderLines]);

    const defaultRow = () => ({ ...blankGridRow(), orderChoice: singleOrderChoice });

    const [rows, setRows] = useState<BatchGridRow[]>([blankGridRow()]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [importNote, setImportNote] = useState<string | null>(null);
    /** The batch that ran short, and the block the server sent back for it. */
    const [blocked, setBlocked] = useState<{ code: string; block: DeclarationBlocked } | null>(
        null,
    );

    /*
     * ONE BLANK ROW ON OPEN, wherever the grid was opened from. A grid reached from one order
     * used to arrive seeded with a row per SKU; the quantities it guessed invited recording an
     * order's whole remainder in one keystroke, so now the person types what was actually
     * made. What opening from an order still supplies: the product picker narrowed to that
     * order's SKUs, and Against pinned to it via `defaultRow`. The ref guard keeps a
     * re-render from wiping somebody's half-typed rows.
     */
    const wasOpened = useRef(false);
    useEffect(() => {
        if (opened && !wasOpened.current) {
            setRows([defaultRow()]);
            setImportNote(null);
            setBlocked(null);
        }
        wasOpened.current = opened;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened]);

    const { data: catalog } = useQuery([scope, 'vendor-catalog'], api.getCatalog, {
        staleTime: 5 * 60 * 1000,
    });

    const { data: materialStock } = useQuery(
        [scope, 'vendor-bucketed-stock', 'material'],
        () => api.getStock({ itemType: 'material' }),
        { enabled: opened, staleTime: 60 * 1000 },
    );

    /*
     * NAMES FOR EVERY PRODUCT, INDEPENDENT OF WHAT IS OFFERED. The shortfall panel below has to
     * name whatever a row holds, including a SKU the picker no longer offers, and reading labels
     * out of the options list would have printed a raw uuid the moment the two diverged.
     */
    const productLabel = useMemo(() => {
        const names = new Map(
            (catalog?.products ?? []).map((product) => [product.itemId, product.name]),
        );
        return (productId: string) => names.get(productId) || productId;
    }, [catalog]);

    const productOptions = useMemo(
        () =>
            restrictProductsToOrder
                ? productOptionsForOrder(orderLines, catalog?.products ?? [])
                : (catalog?.products ?? []).map((product) => ({
                      value: product.itemId,
                      label: product.name ?? product.itemId,
                  })),
        [catalog, orderLines, restrictProductsToOrder],
    );

    /*
     * What one row may choose: the offered list, plus whatever that row already holds.
     *
     * A restricted picker can be handed an off-order SKU by a CSV import, and Mantine renders a
     * value absent from `data` as an empty box - so the row would look like the import had
     * dropped it. Carrying it means it stays visible, and the work order column beside it goes on
     * calling it "Not on that order", which is the honest reading: present and wrong beats gone.
     */
    /** Whether any product here has a case size, which is when the Quantity column makes room for the toggle. */
    const casePackOnOffer = useMemo(
        () => (catalog?.products ?? []).some((product) => product.unitsPerCase),
        [catalog],
    );
    const rowClass = casePackOnOffer ? `${styles.row} ${styles.rowCases}` : styles.row;

    const productOptionsFor = (productId: string) =>
        productId && !productOptions.some((option) => option.value === productId)
            ? [...productOptions, { value: productId, label: productLabel(productId) }]
            : productOptions;

    /*
     * The template teaches by example, so its samples come from the same list the picker offers.
     * Scoped to an order, a sample naming a SKU that order never asked for would come straight
     * back flagged.
     */
    const templateProducts = useMemo(() => {
        const products = catalog?.products ?? [];
        if (!restrictProductsToOrder) return products;
        const onOrder = new Set(productOptions.map((option) => option.value));
        return products.filter((product) => onOrder.has(product.itemId));
    }, [catalog, productOptions, restrictProductsToOrder]);

    // The stock entry exists only where stock runs are allowed; the manufacturer's grid
    // offers orders and nothing else.
    const orderOptions = useMemo(
        () => buildOrderOptions(orderLines, allowStock ? 'To stock' : undefined),
        [orderLines, allowStock],
    );

    const downloadTemplate = () => {
        const blob = new Blob([productionTemplateCsv(templateProducts, orderLines)], {
            type: 'text/csv;charset=utf-8',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'record-production-template.csv';
        link.click();
        URL.revokeObjectURL(url);
    };

    const importCsv = async (file: File | null) => {
        if (!file) return;
        const text = await file.text();
        const {
            rows: parsed,
            unknownHeaders,
            unmatchedProducts,
            missingOrder,
            unknownOrders,
            stockRuns,
        } = parseProductionCsv(text, catalog?.products ?? [], orderLines, allowStock);
        if (parsed.length === 0) {
            setImportNote('Nothing readable in that file.');
            return;
        }

        /*
         * THE WHOLE FILE OR NOTHING. A file that half-imports trains people to ignore the
         * notes; one that comes back naming its blank rows teaches the rule at the exact
         * moment somebody is looking. The rejection is the classroom.
         */
        if (missingOrder.length > 0 || unknownOrders.length > 0 || stockRuns.length > 0) {
            const reasons: string[] = [];
            if (missingOrder.length > 0) {
                reasons.push(
                    `row${missingOrder.length === 1 ? '' : 's'} ${missingOrder.join(', ')} ${
                        missingOrder.length === 1 ? 'has' : 'have'
                    } no Production order — ${
                        allowStock
                            ? 'write the order code, or Stock for a run made ahead'
                            : 'write the order code'
                    }`,
                );
            }
            if (unknownOrders.length > 0) {
                reasons.push(
                    `no open order matches: ${unknownOrders.join(', ')}`,
                );
            }
            if (stockRuns.length > 0) {
                reasons.push(
                    `row${stockRuns.length === 1 ? '' : 's'} ${stockRuns.join(', ')} say${
                        stockRuns.length === 1 ? 's' : ''
                    } Stock — stock runs are not recorded here; every run needs its production order`,
                );
            }
            setImportNote(`Nothing imported — ${reasons.join(' · ')}`);
            return;
        }
        setRows(parsed);
        const notes = [`${parsed.length} row${parsed.length === 1 ? '' : 's'} loaded`];
        if (unmatchedProducts.length > 0) {
            notes.push(`no SKU matched: ${unmatchedProducts.join(', ')}`);
        }
        if (unknownHeaders.length > 0) {
            notes.push(`ignored column${unknownHeaders.length === 1 ? '' : 's'}: ${unknownHeaders.join(', ')}`);
        }
        setImportNote(notes.join(' · '));
    };

    const batches = useMemo(() => groupRowsIntoBatches(rows, orderLines), [rows, orderLines]);
    /**
     * Declarations that resolved to no order, where stock runs are refused. The pickers make
     * this hard to reach — Against has no stock entry and products default to their earliest
     * order — but a CSV or an odd edit can still produce one, and the server would refuse it;
     * this says so before the round trip.
     */
    const orderlessBatches = useMemo(
        () => (allowStock ? [] : batches.filter((batch) => !batch.workOrderId)),
        [allowStock, batches],
    );
    const conflicts = useMemo(() => findConflicts(rows, orderLines), [rows, orderLines]);
    const declarationCheck = useMemo(
        () => checkDeclarations(rows, orderLines, allowStock),
        [rows, orderLines, allowStock],
    );

    /**
     * The one-click way out of an over-attribution: a second row on the same batch carrying
     * the excess to stock. The grid's own model - rows sharing a code are one run - means
     * the fix is a row, not a mechanism.
     */
    const splitExcessToStock = () => {
        setRows((current) => {
            const next = [...current];
            for (const row of current) {
                const over = declarationCheck.overByRow.get(row.id);
                if (!over) continue;
                const quantity = Number(row.quantity) || 0;
                if (quantity < over) continue;

                const index = next.indexOf(row);
                next[index] = { ...row, quantity: String(quantity - over) };
                next.splice(index + 1, 0, {
                    ...blankGridRow(),
                    batchCode: row.batchCode,
                    productId: row.productId,
                    manufacturingDate: row.manufacturingDate,
                    expiryDate: row.expiryDate,
                    quantity: String(over),
                    orderChoice: '',
                });
                // One split per click: the check recomputes and the next click takes the
                // next over-filled row, which keeps each splice trivially reasoned about.
                break;
            }
            return next;
        });
    };

    /**
     * One preview per batch, then summed. The server has no multi-batch preview, and asking it
     * for one answer per batch is correct as far as each batch goes — what it cannot know is
     * that the batch beside it wants the same drum.
     */
    const { data: previews, isFetching: isPreviewing } = useQuery(
        [scope, 'declare-batches-preview', JSON.stringify(batches)],
        async () => {
            const results = await Promise.all(
                batches.map((batch: DeclarationRequest) => api.preview(batch)),
            );
            return results.map((result) => result.consumption);
        },
        { enabled: opened && batches.length > 0, keepPreviousData: true },
    );

    const availableByMaterial = useMemo(() => {
        const map = new Map<string, { name: string; usable: number }>();
        for (const item of materialStock?.items ?? []) {
            map.set(item.itemId, { name: item.itemName ?? item.itemId, usable: item.usable });
        }
        return map;
    }, [materialStock]);

    const draw = useMemo(
        () => buildCumulativeDraw(previews ?? [], availableByMaterial),
        [previews, availableByMaterial],
    );
    const shortages = draw.filter((line) => line.short > 0);

    const update = (id: string, patch: Partial<BatchGridRow>) =>
        setRows((current) => current.map((r) => (r.id === id ? { ...r, ...patch } : r)));

    const addRow = () =>
        setRows((current) => [...current, nextGridRow(current[current.length - 1])]);

    const totalUnits = batches.reduce(
        (sum, batch) => sum + batch.outputs.reduce((s, o) => s + o.quantity, 0),
        0,
    );

    const submit = async () => {
        setIsSubmitting(true);
        // What actually landed, so a failure after it can say so and clear those rows.
        const recorded = new Set<string>();
        try {
            // Sequential, not parallel: each declaration draws from the shelf the one before
            // it left behind, and FEFO has to see them in the order they are listed.
            for (const batch of batches) {
                const result = await api.declare(batch);
                if (result.status === 'BLOCKED') {
                    // The cumulative check makes this rare rather than impossible — stock
                    // moves between preview and submit. Hand the person the same four exits
                    // the single-batch form gives them, named to the batch that stopped.
                    setBlocked({ code: batch.batch.code, block: result });
                    onDeclared();
                    return;
                }
                recorded.add(requestKey(batch));
            }
            showToastSuccess({
                title: `${batches.length} batch${batches.length === 1 ? '' : 'es'} recorded`,
                subtitle: `${formatCount(totalUnits)} units across ${batches.length} run${batches.length === 1 ? '' : 's'}.`,
            });
            onDeclared();
            onClose();
        } catch (err: any) {
            const message = err?.error?.message ?? err?.message ?? 'Something went wrong.';

            /*
             * A FAILURE PART-WAY LEAVES REAL RUNS BEHIND IT, and saying only "could not record
             * the runs" is a lie about the ones that did.
             *
             * The loop posts one declaration at a time, so an error on the third has already
             * committed the first two. Declaring is not idempotent — the server mints a fresh
             * production entry every call — so a grid left whole and submitted again records
             * those two a SECOND time. A silent double-count of production is far worse than
             * the error that caused it: it inflates finished stock, and that flows straight
             * out into dispatch and the brand's ledger.
             *
             * So take the recorded rows off the grid and refresh behind it. What is left is
             * exactly what still needs recording, and the button is safe to press again.
             *
             * The BLOCKED path above already worked this way, in its own panel. This is the
             * same promise for the failure that throws instead.
             */
            if (recorded.size > 0) {
                setRows((current) =>
                    current.filter((row) => !recorded.has(declarationKey(row, orderLines))),
                );
                onDeclared();
            }

            showToastError({
                title:
                    recorded.size > 0
                        ? `${recorded.size} run${recorded.size === 1 ? '' : 's'} recorded, then it stopped`
                        : 'Could not record the runs',
                subtitle:
                    recorded.size > 0
                        ? `${message} The runs that went in have been taken off the grid — what is left is what still needs recording.`
                        : message,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (blocked) {
        return (
            <Modal
                opened={opened}
                onClose={onClose}
                title={`${blocked.code} ran short`}
                size="60rem"
                centered
            >
                <Stack gap={12}>
                    <Text fz={13} c={neutralColors.textSecondary}>
                        Runs listed before this one were recorded. This one and anything after
                        it were not.
                    </Text>
                    <DeclarationBlockedPanel
                        blocked={blocked.block}
                        productNameFor={productLabel}
                        onClose={() => setBlocked(null)}
                        onReleaseQc={(materialId) => onReleaseQc?.([materialId])}
                        onRecordInward={(materialIds) => onRecordInward?.(materialIds)}
                        // The grid has no per-material correction, and inventing one here
                        // would be a second declaration form. Send them back to the rows to
                        // fix what they can, and to the single-batch form for actuals.
                        onEnterActuals={() => setBlocked(null)}
                        onDeclareLess={(outputs) => {
                            // Rewrite this batch's rows to what will actually fit, and let
                            // them resubmit rather than retyping the sheet.
                            setRows((current) =>
                                current.map((row) => {
                                    if (row.batchCode.trim() !== blocked.code) return row;
                                    const reduced = outputs.find(
                                        (o) => o.productId === row.productId,
                                    );
                                    return reduced
                                        ? { ...row, quantity: String(reduced.quantity) }
                                        : row;
                                }),
                            );
                            setBlocked(null);
                        }}
                    />
                </Stack>
            </Modal>
        );
    }

    return (
        <Modal opened={opened} onClose={onClose} title="Record production" size="90rem" centered>
            <Stack gap={16}>
                {onReportedByChange && (
                    <ReportedByField
                        value={reportedBy ?? ''}
                        onChange={onReportedByChange}
                    />
                )}
                <Text fz={13} c={neutralColors.textSecondary}>
                    One row per SKU. Rows sharing a batch code are one run — a mix packed into
                    three sizes is three rows under one code.
                </Text>
                <Text fz={12} c={neutralColors.textMuted}>
                    Uploading a file? It needs <strong>Product</strong>,{' '}
                    <strong>Batch code</strong>, <strong>Quantity</strong> and{' '}
                    <strong>Production order</strong> —{' '}
                    {allowStock ? (
                        <>
                            name the order, or write <strong>Stock</strong> for a run made
                            ahead. A blank order column rejects the file. Only products with
                            an open production order here can be recorded, to stock included.
                        </>
                    ) : (
                        <>
                            every run names the order it answers. A blank order column, or
                            the word Stock, rejects the file — if more was made than ordered,
                            the brand raises another production order.
                        </>
                    )}{' '}
                    Products match on name or code. Download the template to see it filled in
                    with those products.
                </Text>
                <Text fz={12} c={neutralColors.textMuted}>
                    Everything recorded here starts as awaiting QC. It can still be
                    dispatched — the brand checks the load when it arrives — and you will see
                    the verdict against the batch once they have ruled.
                </Text>

                <div className={styles.grid}>
                    <div className={`${rowClass} ${styles.head}`}>
                        <span>Product</span>
                        <span>Batch code</span>
                        <span>Quantity</span>
                        <span>Manufactured</span>
                        <span>Against</span>
                        <span>Work order</span>
                        <span>Expires</span>
                        <span />
                    </div>

                    {rows.map((row) => {
                        const resolved = resolveWorkOrder(
                            row.orderChoice,
                            row.productId,
                            orderLines,
                        );
                        return (
                            <div key={row.id} className={rowClass}>
                                {/*
                                  * PRODUCT FIRST, THEN THE CODE, which is the order a floor
                                  * works in: you know what was made before you know what got
                                  * printed on it. Receive already reads Item first, so this
                                  * is the portal agreeing with itself rather than a new idea.
                                  *
                                  * It also puts the seeding in the right place. Picking the
                                  * product sets Against below, so doing it first settles the
                                  * order before anything else is typed - and on a row added
                                  * beneath, everything carries down EXCEPT the SKU, so the
                                  * first cell reached is the one always needing a keystroke.
                                  */}
                                <Select
                                    data={productOptionsFor(row.productId)}
                                    value={row.productId || null}
                                    placeholder="Product"
                                    searchable
                                    comboboxProps={{ width: 'target', position: 'bottom-start' }}
                                    onChange={(v) =>
                                        update(row.id, {
                                            productId: v ?? '',
                                            // Seeded, not forced: the earliest open order
                                            // still needing this product, or stock when
                                            // every order is covered. Re-picking re-seeds;
                                            // a hand-set choice sticks until then.
                                            orderChoice: v
                                                ? defaultOrderChoiceForProduct(v, orderLines)
                                                : '',
                                        })
                                    }
                                />
                                <TextInput
                                    value={row.batchCode}
                                    placeholder="As printed"
                                    onChange={(e) =>
                                        update(row.id, { batchCode: e.currentTarget.value })
                                    }
                                />
                                <div>
                                    <QuantityInput
                                        value={row.quantity === '' ? '' : Number(row.quantity)}
                                        placeholder="0"
                                        hideControls
                                        min={0}
                                        error={Boolean(declarationCheck.overByRow.get(row.id))}
                                        onChange={(units) =>
                                            update(row.id, {
                                                quantity: units === '' ? '' : String(units),
                                            })
                                        }
                                        unitsPerCase={
                                            catalog?.products.find(
                                                (product) => product.itemId === row.productId,
                                            )?.unitsPerCase ?? null
                                        }
                                    />
                                    {declarationCheck.overByRow.get(row.id) ? (
                                        <Text fz={11} c={semanticColors.critical.primary}>
                                            Over by{' '}
                                            {formatCount(declarationCheck.overByRow
                                                .get(row.id)!)}
                                        </Text>
                                    ) : null}
                                </div>
                                <DateInput
                                    value={row.manufacturingDate || null}
                                    placeholder="YYYY-MM-DD"
                                    valueFormat="YYYY-MM-DD"
                                    clearable
                                    // A run being RECORDED has happened; a future date here is
                                    // a typo, not a plan. Expires stays free - it must be ahead.
                                    maxDate={new Date()}
                                    onChange={(v) =>
                                        update(row.id, { manufacturingDate: v ?? '' })
                                    }
                                />
                                <Select
                                    data={orderOptions}
                                    value={row.orderChoice}
                                    allowDeselect={false}
                                    onChange={(v) => update(row.id, { orderChoice: v ?? '' })}
                                />
                                {/* Read-only: derived from the order and the SKU, never picked.
                                    Shown so nobody has to trust that it landed somewhere. */}
                                <Text
                                    fz={12}
                                    className={styles.workOrderCell}
                                    c={
                                        resolved.unmatched
                                            ? semanticColors.critical.primary
                                            : neutralColors.textSecondary
                                    }
                                >
                                    {resolved.code ??
                                        (resolved.unmatched ? 'Not on that order' : '—')}
                                </Text>
                                <DateInput
                                    value={row.expiryDate || null}
                                    placeholder="YYYY-MM-DD"
                                    valueFormat="YYYY-MM-DD"
                                    clearable
                                    onChange={(v) => update(row.id, { expiryDate: v ?? '' })}
                                />
                                <Button
                                    variant={ButtonVariant.SUBTLE}
                                    size="xs"
                                    disabled={rows.length === 1}
                                    onClick={() =>
                                        setRows((current) =>
                                            current.filter((r) => r.id !== row.id),
                                        )
                                    }
                                >
                                    <TrashIcon size={14} />
                                </Button>
                            </div>
                        );
                    })}
                </div>

                <Group justify="space-between">
                    <Group gap={8}>
                        <Button
                            variant={ButtonVariant.SUBTLE}
                            leftSection={<PlusIcon size={14} />}
                            onClick={addRow}
                        >
                            Add row
                        </Button>
                        {/*
                          * One run packed into several SKUs is the case this grid is built
                          * around, and on a seeded order it means typing the same code once per
                          * row. Offered only when there is more than one row and a code to
                          * spread, so it never appears with nothing to do.
                          */}
                        {rows.length > 1 && rows.some((row) => row.batchCode.trim()) && (
                            <Button
                                variant={ButtonVariant.SUBTLE}
                                onClick={() => setRows((current) => fillDownBatchCode(current))}
                            >
                                Same batch for all
                            </Button>
                        )}
                        <Button
                            variant={ButtonVariant.SUBTLE}
                            leftSection={<DownloadSimpleIcon size={14} />}
                            onClick={downloadTemplate}
                        >
                            Template
                        </Button>
                        <FileButton accept=".csv,text/csv" onChange={importCsv}>
                            {(props) => (
                                <Button
                                    {...props}
                                    variant={ButtonVariant.SUBTLE}
                                    leftSection={<UploadSimpleIcon size={14} />}
                                >
                                    Upload CSV
                                </Button>
                            )}
                        </FileButton>
                    </Group>
                    <Text fz={13} c={neutralColors.textSecondary}>
                        {batches.length} batch{batches.length === 1 ? '' : 'es'} ·{' '}
                        {formatCount(totalUnits)} units
                    </Text>
                </Group>

                {importNote && (
                    <Text fz={12} c={neutralColors.textSecondary}>
                        {importNote}
                    </Text>
                )}

                {conflicts.length > 0 && (
                    <Stack gap={4}>
                        {conflicts.map((conflict) => (
                            <Text
                                key={`${conflict.batchCode}-${conflict.field}`}
                                fz={12}
                                c={semanticColors.warning.dark}
                            >
                                {conflict.batchCode} has more than one{' '}
                                {conflict.field === 'expiryDate' ? 'expiry' : 'manufacturing'}{' '}
                                date. The first row wins.
                            </Text>
                        ))}
                    </Stack>
                )}

                {orderlessBatches.length > 0 && (
                    <Text fz={12} c={semanticColors.critical.primary}>
                        {orderlessBatches.length === 1
                            ? 'One run has'
                            : `${orderlessBatches.length} runs have`}{' '}
                        no production order selected — pick the order each run answers.
                    </Text>
                )}

                {declarationCheck.overfilled.length > 0 && (
                    <Stack gap={4}>
                        {declarationCheck.overfilled.map((message) => (
                            <Text fz={12} c={semanticColors.critical.primary} key={message}>
                                {message}
                            </Text>
                        ))}
                        {/* The one-click fix exists only where stock runs do. Without it the
                            over-entry is a hard stop, and the message says the honest way
                            out: reduce, or ask the brand for another order. */}
                        {allowStock ? (
                            <div>
                                <Button
                                    size="xs"
                                    variant={ButtonVariant.DEFAULT}
                                    onClick={splitExcessToStock}
                                >
                                    Split the rest to stock
                                </Button>
                            </div>
                        ) : (
                            <Text fz={12} c={neutralColors.textMuted}>
                                Reduce the quantity — if more was made than ordered, the
                                brand raises another production order to cover it.
                            </Text>
                        )}
                    </Stack>
                )}

                <Stack gap={8}>
                    <Group gap={8}>
                        <Text fz={14} fw={600} c={neutralColors.textPrimary}>
                            What all of this will use
                        </Text>
                        {isPreviewing && <Loader size="xs" />}
                    </Group>
                    {draw.length === 0 && (
                        <Text fz={13} c={neutralColors.textMuted}>
                            Fill in a product, a batch code and a quantity to see the draw.
                        </Text>
                    )}
                    {draw.map((line) => (
                        <Group key={line.materialId} justify="space-between">
                            <Text fz={13} c={neutralColors.textPrimary}>
                                {line.materialName}
                            </Text>
                            <Text
                                fz={13}
                                c={
                                    line.short > 0
                                        ? semanticColors.critical.primary
                                        : neutralColors.textSecondary
                                }
                            >
                                {formatCount(line.required)} needed ·{' '}
                                {formatCount(line.available)} usable
                                {line.short > 0
                                    ? ` · ${formatCount(line.short)} short`
                                    : ''}
                            </Text>
                        </Group>
                    ))}
                </Stack>

                <Group justify="flex-end" gap={8}>
                    <Button variant={ButtonVariant.SUBTLE} onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        variant={ButtonVariant.ACTION}
                        loading={isSubmitting}
                        disabled={
                            batches.length === 0 ||
                            shortages.length > 0 ||
                            // Over-attribution blocks: Manufactured must never exceed
                            // Ordered on the books, whoever keys it in.
                            declarationCheck.overfilled.length > 0 ||
                            // A run with no order cannot be recorded from this grid.
                            orderlessBatches.length > 0 ||
                            // Only the brand supplies the setter, and only the brand has to
                            // name a reporter — so an empty box blocks nobody else.
                            (!!onReportedByChange && !reportedBy?.trim())
                        }
                        onClick={submit}
                    >
                        Record {batches.length || ''} run
                        {batches.length === 1 ? '' : 's'}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};

export default DeclareBatchesModal;
